import { defineStore } from "pinia";
import { ref } from "vue";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useAuthStore } from "./auth";

/**
 * Contexte d'espace de travail (Lot 3 — partage sans migration).
 *
 * Sépare QUI agit (actorUid) de OÙ vivent les données (dataOwnerUid). Toutes
 * les lectures temps réel (onSnapshot) doivent viser dataOwnerUid ; toutes les
 * écritures passent par des Cloud Functions.
 *
 * En mode solo (aucune compagnie OU partage désactivé), dataOwnerUid === actor
 * → comportement identique à avant. Le serveur (getWorkspaceContext) tranche.
 */
export const useWorkspaceStore = defineStore("workspace", () => {
  const actorUid = ref<string | null>(null);
  const dataOwnerUid = ref<string | null>(null);
  const companyId = ref<string | null>(null);
  const role = ref<"owner" | "member">("owner");
  const scope = ref<"solo" | "shared">("solo");
  /** Config commune du créateur (logo, nom, taxes…) en mode partagé ; null en solo. */
  const ownerSettings = ref<Record<string, any> | null>(null);
  const loaded = ref(false);
  let loadingPromise: Promise<void> | null = null;

  async function ensureLoaded(force = false): Promise<void> {
    if (loaded.value && !force) return;
    if (loadingPromise && !force) return loadingPromise;

    loadingPromise = (async () => {
      const auth = useAuthStore();
      if (!auth.user) return;
      // Repli solo par défaut : si la CF échoue, on reste sur le compte courant.
      actorUid.value = auth.user.uid;
      dataOwnerUid.value = auth.user.uid;
      companyId.value = null;
      role.value = "owner";
      scope.value = "solo";
      ownerSettings.value = null;
      try {
        const call = httpsCallable<Record<string, never>, any>(functions, "getWorkspaceContext");
        const res = await call({});
        actorUid.value = res.data.actorUid ?? auth.user.uid;
        dataOwnerUid.value = res.data.dataOwnerUid ?? auth.user.uid;
        companyId.value = res.data.companyId ?? null;
        role.value = res.data.role === "member" ? "member" : "owner";
        scope.value = res.data.scope === "shared" ? "shared" : "solo";
        ownerSettings.value = res.data.ownerSettings ?? null;
      } catch {
        // Repli solo déjà en place.
      }
      loaded.value = true;
    })();

    try {
      await loadingPromise;
    } finally {
      loadingPromise = null;
    }
  }

  /** uid où lire/écrire les données (avec repli sur l'acteur). */
  function ownerUid(): string | null {
    return dataOwnerUid.value ?? useAuthStore().user?.uid ?? null;
  }

  /**
   * Config boutique effective (logo, nom, taxes, shareText, defaultCosts…) :
   * celle du créateur en mode partagé, sinon celle de l'utilisateur courant.
   */
  function shopSettings(): Record<string, any> {
    if (scope.value === "shared" && ownerSettings.value) return ownerSettings.value;
    return (useAuthStore().profile?.settings as Record<string, any>) ?? {};
  }

  function reset(): void {
    loaded.value = false;
    loadingPromise = null;
    actorUid.value = null;
    dataOwnerUid.value = null;
    companyId.value = null;
    role.value = "owner";
    scope.value = "solo";
    ownerSettings.value = null;
  }

  return {
    actorUid, dataOwnerUid, companyId, role, scope, ownerSettings, loaded,
    ensureLoaded, ownerUid, shopSettings, reset,
  };
});
